import {
  Invitation,
  InvitationAttendeeState,
  InvitationState,
  InvitationTicket,
  InvitationTranslations,
  OrderStatus,
  OrderType,
  Prisma,
  prisma,
  TokenableModel,
  TransitionalModel,
  TransitionState,
  InvitationAttendeeCategoryState,
  InvitationAttendeeCategory,
  VerificationToken,
  RSVPvalues,
  OrderItemStatus,
  OrderItem,
  RSVP,
  FormResultStatus,
  FormResultType,
  reportingPrisma,
} from "client";
import { ReminderBehaviorEnum } from "client";
import { isSameDay } from "date-fns";
import { generateOrderNumber } from "services/order";
import {
  getInvitationAttendeeStatusCounts,
  getInvitationAttendeeStatusCountsByEventId,
  type InvitationAttendeeStatusCounts,
} from "./getInvitationAttendeeStatusCounts";
import {
  createOrderItemTransition,
  createOrderTransition,
  getLatestTransition,
} from "services/Transitions";
import { generateToken } from "services/verificationToken";
import { startOfDay, endOfDay } from "date-fns";
import { setupParticipantFeatures, findExistingProfile } from "services/profile/helper";
import { getWhiteLabelByEventId } from "services/white-label";
import { adoptUnscopedUser } from "services/user/scopedUser";
import {
  enrichInvitationWithTemplateV2,
  getRSVPTemplateV2IdForInvitation,
  getTemplateV2LinkedToInvitation,
  resolveTemplateV2,
  syncInvitationTemplateV2Targets,
  syncRSVPTemplateV2ForInvitation,
  unlinkRSVPTemplatesV2ForInvitation,
} from "services/templateV2";
import {
  TemplateTargetEntityType,
  TemplateV2For,
  TemplateV2Type,
} from "client";
import { invalidateCheckinRosterForProfile } from "services/checkInOut/warmCheckinCache";

/**
 * Returns true when the invitation has a fixed expiry date in the past.
 * Invitations without an `expiryDate` are treated as non-expiring.
 *
 * Centralizing this check keeps the page, the API response handler, the
 * resend action, and the cron lookup in agreement about what "expired" means.
 */
export const isInvitationExpired = (
  expiryDate: Date | string | null | undefined
): boolean => {
  if (!expiryDate) return false;
  const expiry =
    expiryDate instanceof Date ? expiryDate : new Date(expiryDate);
  if (Number.isNaN(expiry.getTime())) return false;
  return expiry.getTime() <= Date.now();
};

/**
 * Updates only the invitation's `expiryDate` and propagates the change to any
 * open derived orders (NEW/ON_GOING) so the expire-order cron immediately
 * respects the new deadline. Completed/canceled orders are left as-is.
 */
export const updateInvitationExpiryDate = async (
  invitationId: string,
  expiryDate: Date | null,
  userId: string
) => {
  return prisma.$transaction(async (tx) => {
    const updated = await tx.invitation.update({
      where: { id: invitationId },
      data: { expiryDate, updatedById: userId },
    });

    await tx.order.updateMany({
      where: {
        type: OrderType.INVITATION,
        status: { in: [OrderStatus.NEW, OrderStatus.ON_GOING] },
        orderItems: {
          some: { invitationAttendee: { invitationId } },
        },
      },
      data: { expiryDate },
    });

    return updated;
  });
};

const syncInvitationRsvps = async (
  tx: Prisma.TransactionClient,
  params: {
    invitationId: string;
    eventId: string;
    RSVPvalues: RSVPvalues[];
  }
) => {
  const existingRsvps = await tx.rSVP.findMany({
    where: { invitationId: params.invitationId },
  });

  const existingByValue = new Map(
    existingRsvps.map((rsvp) => [rsvp.value, rsvp])
  );
  const desiredValues = new Set(params.RSVPvalues);

  const rsvpsToRemove = existingRsvps.filter(
    (rsvp) => !desiredValues.has(rsvp.value)
  );
  const rsvpValuesToAdd = params.RSVPvalues.filter(
    (value) => !existingByValue.has(value)
  );

  if (!rsvpsToRemove.length && !rsvpValuesToAdd.length) {
    return existingRsvps;
  }

  if (rsvpsToRemove.length) {
    await tx.rSVP.deleteMany({
      where: {
        invitationId: params.invitationId,
        value: { in: rsvpsToRemove.map((rsvp) => rsvp.value) },
      },
    });

    await Promise.all(
      rsvpsToRemove.map((rsvp) =>
        syncRSVPTemplateV2ForInvitation(tx, {
          eventId: params.eventId,
          invitationId: params.invitationId,
          rsvpValue: rsvp.value,
          templateId: null,
        })
      )
    );
  }

  if (rsvpValuesToAdd.length) {
    await tx.rSVP.createMany({
      data: rsvpValuesToAdd.map((value) => ({
        invitationId: params.invitationId,
        value,
      })),
    });
  }

  return tx.rSVP.findMany({
    where: { invitationId: params.invitationId },
  });
};

export const getInvitationById = async (id: string) => {
  try {
    const invitation = await prisma.invitation.findFirst({
      where: {
        id: id,
      },
      include: {
        verificationTokens: true,
        invitationTickets: {
          include: {
            ticket: true,
          },
        },

        attendees: {
          select: {
            id: true,
          },
        },
        translations: true,
        rsvps: true,
        event: {
          include: {
            whiteLabel: true,
          },
        },
      },
    });

    if (!invitation) {
      return invitation;
    }

    return enrichInvitationWithTemplateV2(invitation);
  } catch (e: any) {
    console.error(e);
    throw new Error(e);
  }
};
export const getInvitationByToken = async (token: string) => {
  const invitation = await prisma.invitation.findFirst({
    where: {
      verificationTokens: {
        some: {
          token,
        },
      },
    },
    include: {
      invitationTickets: {
        include: {
          ticket: {
            select: {
              id: true,
            },
          },
        },
      },
      attendees: {
        select: {
          id: true,
        },
      },
      translations: true,
      verificationTokens: true,
    },
  });

  return invitation;
};
export const getInvitationByAttendeeId = async (
  attendeeId: string,
  token?: string
) => {
  let invitation;
  if (token) invitation = await getInvitationByToken(token);
  else
    invitation = await prisma.invitation.findFirst({
      where: {
        attendees: {
          some: {
            id: attendeeId,
          },
        },
      },
      include: {
        invitationTickets: {
          include: {
            ticket: {
              select: {
                id: true,
              },
            },
          },
        },
        verificationTokens: true,
        translations: true,
      },
    });

  return invitation;
};
export const getInvitationsByEventId = async (
  userId: string,
  eventId: string,
  lang?: string,
  include?: Prisma.InvitationInclude
) => {
  const invitations = await prisma.invitation.findMany({
    where: {
      eventId: eventId,
    },
    include: {
      invitationTickets: true,
      attendees: true,
      translations: lang
        ? {
            where: {
              language: {
                code: lang,
              },
            },
          }
        : true,
      formEntity: {
        include: {
          formTemplate: {
            include: {
              translations: true,
            },
          },
        },
      },
      verificationTokens: true,
      rsvps: {
        include: {
          notificationTemplate: true,
          thankYouPageTemplate: true,
        },
      },
      ...include,
    },
  });

  const invitationsWithTemplates = await Promise.all(
    invitations.map((invitation) => enrichInvitationWithTemplateV2(invitation))
  );

  // Filter on the FK we already hold (InvitationAttendee(invitationId, state)) instead of
  // `invitation: { eventId }`, which $lookups Invitation per attendee row.
  const rsvpCounts =
    invitations.length > 0
      ? await prisma.invitationAttendee.groupBy({
          by: ["invitationId", "state"],
          where: {
            invitationId: { in: invitations.map((invitation) => invitation.id) },
          },
          _count: {
            state: true,
          },
        })
      : [];

  const rsvpCountMap = rsvpCounts.reduce(
    (acc, { invitationId, state, _count }) => {
      if (!acc[invitationId]) acc[invitationId] = {};
      acc[invitationId][state] = _count.state;
      return acc;
    },
    {} as Record<string, Record<string, number>>
  );

  return invitationsWithTemplates.map((invitation) => ({
    ...invitation,
    ...(lang ? invitation.translations[0] : {}),
    id: invitation.id,
    hasForm: !!invitation.formEntity,
    rsvpCounts: rsvpCountMap[invitation.id] || {},
  }));
};

export interface ScheduleInvitationWithTranslations
  extends InvitationAttendeeCategory {
  invitation: Invitation & {
    translations: InvitationTranslations[];
  } & {
    verificationTokens?: VerificationToken[];
  } & { rsvps?: RSVP[] } & { invitationTickets?: InvitationTicket[] };
  state: InvitationAttendeeCategoryState;
  isScheduled: boolean;
}
export const getScheduleInvitations = async (): Promise<
  ScheduleInvitationWithTranslations[]
> => {
  const currentDate = new Date();
  const invitationAttendeeCategories =
    await prisma.invitationAttendeeCategory.findMany({
      where: {
        AND: [
          {
            expiryDate: { gt: currentDate },
            scheduleDate: { lte: currentDate },
            state: { notIn: [InvitationState.SENT, InvitationState.DRAFT] },
          },
        ],
      },
      include: {
        invitation: {
          include: {
            verificationTokens: true,
            translations: true,
            rsvps: true,
            invitationTickets: {
              include: {
                ticket: true,
              },
            },
          },
        },
      },
    });

  return invitationAttendeeCategories.map((category) => ({
    ...category,
    invitation: { ...category.invitation },
    state: category.state,
    isScheduled: category.scheduleDate
      ? isSameDay(currentDate, category.scheduleDate)
      : false,
  }));
};

export const getReminderScheduledInvitations = async (): Promise<
  ScheduleInvitationWithTranslations[]
> => {
  const currentDate = new Date();
  const invitationAttendeeCategories =
    await prisma.invitationAttendeeCategory.findMany({
      where: {
        AND: [
          {
            expiryDate: { gt: currentDate },
            OR: [
              {
                OR: [
                  {
                    reminderDate: {
                      gte: startOfDay(currentDate),
                      lte: endOfDay(currentDate),
                    },
                    OR: [
                      {
                        lastReminderSentAt: { isSet: false },
                      },
                      {
                        lastReminderSentAt: null,
                      },
                    ],
                  },
                  {
                    OR: [
                      {
                        // Check if reminder date + 1 day matches the current date
                        reminderBehavior: ReminderBehaviorEnum.EVERY_DAY,
                        reminderDate: {
                          gte: startOfDay(addDays(currentDate, 1)),
                          lte: endOfDay(addDays(currentDate, 1)),
                        },
                        lastReminderSentAt: {
                          lt: startOfDay(addDays(currentDate, 1)),
                        },
                      },
                      {
                        // Check if reminder date + 2 days matches the current date
                        reminderBehavior: ReminderBehaviorEnum.EVERY_TWO_DAYS,
                        reminderDate: {
                          gte: startOfDay(addDays(currentDate, 2)),
                          lte: endOfDay(addDays(currentDate, 2)),
                        },
                        lastReminderSentAt: {
                          lt: startOfDay(addDays(currentDate, 2)),
                        },
                      },
                      {
                        // Check if reminder date + 1 week matches the current date
                        reminderBehavior: ReminderBehaviorEnum.ONCE_A_WEEK,
                        reminderDate: {
                          gte: startOfDay(addDays(currentDate, 7)),
                          lte: endOfDay(addDays(currentDate, 7)),
                        },
                        lastReminderSentAt: {
                          lt: startOfDay(addDays(currentDate, 7)),
                        },
                      },
                    ],
                  },
                ],
              },
            ],
          },
        ],
      },
      include: {
        invitation: {
          include: {
            translations: true,
            verificationTokens: true,
          },
        },
      },
    });

  return invitationAttendeeCategories.map((category) => ({
    ...category,
    invitation: { ...category.invitation },
    state: category.state,
    isScheduled: category.scheduleDate
      ? isSameDay(currentDate, category.scheduleDate)
      : false,
  }));
};
export const createInvitationAttendees = async (invitationId: string) => {
  try {
    const invitation = await prisma.invitation.findUnique({
      where: {
        id: invitationId,
      },
      include: {
        invitationAttendeeCategories: {
          include: {
            contactBookGroup: {
              include: {
                contacts: true,
              },
            },
          },
        },
      },
    });
    if (!invitation) {
      throw new Error(`Invitation with id ${invitationId} not found`);
    }
    // Prepare the data for creating invitation attendees
    const attendeesData =
      invitation.invitationAttendeeCategories?.flatMap((category) => {
        const contacts = category.contactBookGroup?.contacts ?? [];
        if (contacts.length === 0) {
          console.warn(
            `No contacts found for attendee category "${category.id}".`
          );
          return [];
        }

        return contacts.map((contact) => ({
          attendeesCategoryId: category.id,
          contactId: contact.id,
          invitationId: invitation.id,
        }));
      }) || [];

    if (attendeesData.length === 0) {
      console.info(`No attendees to create for invitation "${invitationId}".`);
      return { message: "No attendees created." };
    }

    // Check for existing attendees
    const existingAttendees = await prisma.invitationAttendee.findMany({
      where: {
        AND: [
          {
            attendeesCategoryId: {
              in: attendeesData.map((a) => a.attendeesCategoryId),
            },
          },
          { contactId: { in: attendeesData.map((a) => a.contactId) } },
          { invitationId: { in: attendeesData.map((a) => a.invitationId) } },
        ],
      },
    });
    const existingAttendeeMap = new Map(
      existingAttendees.map((existing) => [
        JSON.stringify({
          attendeesCategoryId: existing.attendeesCategoryId,
          contactId: existing.contactId,
          invitationId: existing.invitationId,
        }),
        existing,
      ])
    );

    // Filter out already existing attendees
    const newAttendeesData = attendeesData.filter(
      (attendee) =>
        !existingAttendeeMap.has(
          JSON.stringify({
            attendeesCategoryId: attendee.attendeesCategoryId,
            contactId: attendee.contactId,
            invitationId: attendee.invitationId,
          })
        )
    );

    if (newAttendeesData.length === 0) {
      console.info(
        `No new attendees to create for invitation "${invitationId}".`
      );
      return { message: "No new attendees created." };
    }

    // Create the attendees in the database
    const createdAttendees = await prisma.invitationAttendee.createMany({
      data: newAttendeesData,
    });

    return createdAttendees;
  } catch (e) {
    console.error(e);
  }
};
export const getInvitationAttendees = async (
  invitationId: string,
  categoryId: string
) => {
  try {
    const attendees = await prisma.invitationAttendee.findMany({
      where: {
        invitationId: invitationId,
        state: InvitationAttendeeState.CREATED,
        attendeesCategoryId: categoryId,
      },
      include: {
        contact: true,
      },
    });
    return attendees;
  } catch (e) {
    console.error(e);
  }
};
export const getReminderInvitationAttendees = async (
  invitationId: string,
  categoryId: string
) => {
  try {
    const attendees = await prisma.invitationAttendee.findMany({
      where: {
        invitationId: invitationId,
        state: {
          in: [InvitationAttendeeState.SENT, InvitationAttendeeState.MAYBE],
        },
        attendeesCategoryId: categoryId,
      },
      include: {
        contact: true,
      },
    });
    return attendees;
  } catch (e) {}
};
export const getInvitationAttendee = async (
  invitationId: string,
  attendeeId: string
) => {
  try {
    const attendees = await prisma.invitationAttendee.findFirst({
      where: {
        id: attendeeId,
        invitationId: invitationId,
      },
      include: {
        contact: true,
      },
    });
    return attendees;
  } catch (e) {
    console.error(e);
  }
};
// POST

export const createInvitation = async (
  invitationPayload: Prisma.InvitationCreateInput,
  translations: Prisma.InvitationTranslationsCreateInput[],
  createdById: string,
  eventId: string,
  ticketsQuantities?: any[],
  attendeeIds?: string[],
  tokenable?: boolean,
  RSVPvalues?: RSVPvalues[],
  emailTemplate?: string[],
  SMStemplate?: string[],
  whatsappTemplate?: string[],
  physicalTemplate?: string[],
  fromEntityId?: string
) => {
  try {
    const updatedTranslations = await Promise.all(
      translations.map(async (translation) => {
        const baseName = translation.name.trim();
        let finalName = baseName;
        let counter = 1;

        while (await getInvitationTranslationByName(finalName, eventId)) {
          finalName = `${baseName}-${counter}`;
          counter++;
        }

        return {
          ...translation,
          name: finalName,
        };
      })
    );

    const templateIds = [
      ...(emailTemplate ?? []),
      ...(SMStemplate ?? []),
      ...(whatsappTemplate ?? []),
      ...(physicalTemplate ?? []),
    ].filter(Boolean);

    const invitationData: any = {
      ...invitationPayload,
      event: {
        connect: { id: eventId },
      },
      attendees: {
        connect: attendeeIds ? attendeeIds.map((id) => ({ id })) : undefined,
      },
      createdBy: {
        connect: { id: createdById },
      },
      ...(ticketsQuantities?.length
        ? {
            invitationTickets: {
              createMany: {
                data: ticketsQuantities.map((ticketsQuantity) => ({
                  ticketId: ticketsQuantity.ticketId,
                  quantity: ticketsQuantity.quantity,
                })),
              },
            },
          }
        : {}),
      translations: {
        createMany: {
          data: updatedTranslations,
        },
      },
      ...(RSVPvalues?.length
        ? {
            rsvps: {
              createMany: {
                data: RSVPvalues.map((rsvp) => ({
                  value: rsvp,
                })),
              },
            },
          }
        : {}),
      ...(fromEntityId
        ? {
            formEntity: {
              connect: { id: fromEntityId },
            },
          }
        : {}),
    };

    // Use transaction to ensure atomicity
    const invitation = await prisma.$transaction(
      async (tx) => {
        // Create invitation
        const newInvitation = await tx.invitation.create({
          data: invitationData,
          include: {
            rsvps: true,
          },
        });

        if (templateIds.length) {
          await syncInvitationTemplateV2Targets(tx, {
            eventId,
            invitationId: newInvitation.id,
            templateIds,
          });
        }

        return newInvitation;
      },
      { timeout: 10000, maxWait: 5000 }
    );

    if (tokenable) {
      await generateToken(invitation.id, TokenableModel.INVITATION);
    }

    return enrichInvitationWithTemplateV2(invitation);
  } catch (e: any) {
    console.error("Error creating invitation:", e);
    throw new Error(e.message);
  }
};

export const getInvitationTranslationByName = async (
  name: string,
  eventId: string,
  excludeInvitationId?: string
) => {
  try {
    return await prisma.invitationTranslations.findFirst({
      where: {
        name,
        invitation: {
          eventId,
        },
        ...(excludeInvitationId && {
          invitationId: {
            not: excludeInvitationId,
          },
        }),
      },
    });
  } catch (e: any) {
    console.error(e);
    throw new Error(e.message);
  }
};

export const updateInvitation = async (
  id: string,
  updatedData: Prisma.InvitationUpdateInput,
  translations: Prisma.InvitationTranslationsUncheckedUpdateManyInput[],
  ticketsQuantities: { ticketId: string; quantity: number }[],
  tokenable: boolean,
  userId: string,
  linkable: boolean,
  RSVPvalues?: RSVPvalues[],
  emailTemplate?: string[],
  SMStemplate?: string[],
  whatsappTemplate?: string[],
  physicalTemplate?: string[],
  fromEntityId?: string
) => {
  try {
    const existingInvitation = await prisma.invitation.findUnique({
      where: { id },
      include: {
        invitationTickets: { select: { ticketId: true } },
        formEntity: true,
        verificationTokens: true,
        attendees: true,
        event: true,
      },
    });

    if (!existingInvitation) throw new Error("Invitation not found");

    const updatedTranslations = await Promise.all(
      translations.map(async (translation) => {
        const baseName = (translation.name as string)?.trim();
        let finalName = baseName;
        let counter = 1;

        while (
          await getInvitationTranslationByName(
            finalName,
            existingInvitation.event?.id,
            id
          )
        ) {
          finalName = `${baseName}-${counter}`;
          counter++;
        }

        return { ...translation, name: finalName };
      })
    );

    const existingTicketIds = existingInvitation.invitationTickets.map(
      (t) => t.ticketId
    );

    const ticketsToCreate = ticketsQuantities.filter(
      (t) => !existingTicketIds.includes(t.ticketId)
    );

    const ticketsToUpdate = ticketsQuantities.filter((t) =>
      existingTicketIds.includes(t.ticketId)
    );

    const ticketsToDelete = existingTicketIds.filter(
      (ticketId) => !ticketsQuantities.some((t) => t.ticketId === ticketId)
    );

    if (!linkable) updatedData.link = null;

    const shouldSyncTemplates =
      emailTemplate !== undefined ||
      SMStemplate !== undefined ||
      whatsappTemplate !== undefined ||
      physicalTemplate !== undefined;

    const templateIds = shouldSyncTemplates
      ? [
          ...(emailTemplate ?? []),
          ...(SMStemplate ?? []),
          ...(whatsappTemplate ?? []),
          ...(physicalTemplate ?? []),
        ].filter(Boolean)
      : [];

    let formEntityUpdate = {};
    if (fromEntityId && existingInvitation.formEntity?.id !== fromEntityId) {
      formEntityUpdate = {
        formEntity: {
          ...(existingInvitation.formEntity && { disconnect: true }),
          connect: { id: fromEntityId },
        },
      };
    }

    // 🔄 Use transaction for atomic safety
    const updatedInvitation = await prisma.$transaction(
      async (tx) => {
        const invitation = await tx.invitation.update({
          where: { id },
          data: {
            ...updatedData,
            updatedById: userId,
            translations: {
              updateMany: updatedTranslations?.map((data) => ({
                where: {
                  invitationId: id,
                  languageId: data.languageId as string,
                },
                data: { name: data.name, description: data.description },
              })),
            },
            invitationTickets: {
              ...(ticketsToCreate.length > 0 && {
                createMany: {
                  data: ticketsToCreate.map((t) => ({
                    ticketId: t.ticketId,
                    quantity: t.quantity,
                  })),
                },
              }),
              ...(ticketsToDelete.length > 0 && {
                deleteMany: ticketsToDelete.map((ticketId) => ({
                  ticketId,
                  invitationId: id,
                })),
              }),
            },
            ...formEntityUpdate,
          },
          include: {
            formEntity: true,
            rsvps: true,
          },
        });

        if (shouldSyncTemplates) {
          await syncInvitationTemplateV2Targets(tx, {
            eventId: existingInvitation.eventId,
            invitationId: id,
            templateIds,
          });
        }

        const rsvps = RSVPvalues?.length
          ? await syncInvitationRsvps(tx, {
              invitationId: id,
              eventId: existingInvitation.eventId,
              RSVPvalues,
            })
          : invitation.rsvps;

        // 🔧 Update ticket quantities one by one
        for (const t of ticketsToUpdate) {
          await tx.invitationTicket.update({
            where: {
              invitationId_ticketId: {
                ticketId: t.ticketId,
                invitationId: id,
              },
            },
            data: {
              quantity: t.quantity,
            },
          });
        }

        // Keep open invitation orders (NEW/ON_GOING) in sync with the
        // invitation's fixed expiry so the expire-order cron applies the new
        // deadline immediately. COMPLETED/CANCELED/EXPIRED orders are left
        // untouched on purpose.
        if (Object.prototype.hasOwnProperty.call(updatedData, "expiryDate")) {
          const nextExpiry =
            (updatedData as { expiryDate?: Date | string | null }).expiryDate ??
            null;

          await tx.order.updateMany({
            where: {
              type: OrderType.INVITATION,
              status: { in: [OrderStatus.NEW, OrderStatus.ON_GOING] },
              orderItems: {
                some: { invitationAttendee: { invitationId: id } },
              },
            },
            data: { expiryDate: nextExpiry as Date | null },
          });
        }

        return {
          ...invitation,
          rsvps,
        };
      },
      { timeout: 10000, maxWait: 5000 }
    );

    if (tokenable && !existingInvitation.verificationTokens[0]?.token) {
      await generateToken(updatedInvitation.id, TokenableModel.INVITATION);
    }

    return enrichInvitationWithTemplateV2(updatedInvitation);
  } catch (e: any) {
    console.error(e);
    throw new Error(e.message);
  }
};

// export const getAttendeeByAccessCode = async (accessCode: string) => {
//   try {
//     const attendee = await prisma.invitationAttendee.findFirst({
//       where: {
//         accessCode: accessCode,
//         ActivateCodeAt: null,
//       },
//     });
//     return attendee;
//   } catch (e: any) {
//     throw new Error(e);
//   }
// };
// export const activateAttendeeAccessCode = async (id: string, code: string) => {
//   try {
//     const attendee = await getAttendeeByAccessCode(code);
//     if (attendee) {
//       const activateAttendee = await prisma.invitationAttendee.update({
//         where: {
//           id,
//         },
//         data: {
//           ActivateCodeAt: new Date(),
//         },
//       });
//     } else {
//       throw new Error(`Attendee with id ${id} not found`);
//     }
//     return attendee;
//   } catch (e: any) {
//     throw new Error(e);
//   }
// };
// Function to add days to a date
function addDays(date: Date, days: number) {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
}
export const getInvitationAttendeeOrder = async (id: string) => {
  try {
    const order = await prisma.order.findFirst({
      where: {
        orderItems: {
          some: {
            invitationAttendeeId: id,
          },
        },
      },
      include: {
        orderItems: {
          include: {
            formResults: {
              include: {
                formResult: true,
              },
            },
          },
        },
        formResults: {
          include: {
            formResult: true,
          },
        },
      },
    });
    return order;
  } catch (e: any) {
    throw new Error(e);
  }
};

const isInvitationFormCompleted = async (
  invitationAttendeeId: string,
  formTemplateId: string
): Promise<boolean> => {
  const completedForm = await prisma.formResultEntity.findFirst({
    where: {
      type: FormResultType.ORDER_ITEM,
      orderItem: { invitationAttendeeId },
      formResult: {
        formTemplateId,
        status: FormResultStatus.COMPLETED,
      },
    },
    select: { id: true },
  });

  return Boolean(completedForm);
};

const resolveInvitationAttendeeParticipantProfile = async (
  attendee: {
    id: string;
    contactId: string | null;
    contact: {
      id: string;
      name?: string | null;
      email?: string | null;
      phoneNumber?: string | null;
      company?: string | null;
      jobTitle?: string | null;
    } | null;
  },
  eventId: string
): Promise<{
  profileId: string;
  contact: NonNullable<typeof attendee.contact>;
} | null> => {
  if (attendee.contact?.id) {
    return { profileId: attendee.contact.id, contact: attendee.contact };
  }

  const email = attendee.contact?.email;
  const phoneNumber = attendee.contact?.phoneNumber;
  if (!email && !phoneNumber) {
    return null;
  }

  const existingProfile = await findExistingProfile(
    email,
    phoneNumber,
    eventId
  );

  if (existingProfile) {
    await prisma.invitationAttendee.update({
      where: { id: attendee.id },
      data: { contactId: existingProfile.id },
    });

    return {
      profileId: existingProfile.id,
      contact: {
        id: existingProfile.id,
        name: existingProfile.name,
        email: existingProfile.email,
        phoneNumber: existingProfile.phoneNumber,
        company: existingProfile.company,
        jobTitle: existingProfile.jobTitle,
      },
    };
  }

  if (!attendee.contact) {
    return null;
  }

  const createdProfile = await prisma.profile.create({
    data: {
      name: attendee.contact.name,
      email: attendee.contact.email,
      phoneNumber: attendee.contact.phoneNumber,
      company: attendee.contact.company,
      jobTitle: attendee.contact.jobTitle,
      event: { connect: { id: eventId } },
    },
  });

  await prisma.invitationAttendee.update({
    where: { id: attendee.id },
    data: { contactId: createdProfile.id },
  });

  return {
    profileId: createdProfile.id,
    contact: {
      id: createdProfile.id,
      name: createdProfile.name,
      email: createdProfile.email,
      phoneNumber: createdProfile.phoneNumber,
      company: createdProfile.company,
      jobTitle: createdProfile.jobTitle,
    },
  };
};

export const addInvitationAttendeeToParticipantGroups = async (
  profileId: string,
  participantGroupIds: string[],
  eventId: string,
  contactData?: {
    name?: string | null;
    email?: string | null;
    phoneNumber?: string | null;
    company?: string | null;
    jobTitle?: string | null;
  }
): Promise<{ token?: string }> => {
  if (participantGroupIds.length === 0) {
    return {};
  }

  const existingProfile = await prisma.profile.findUnique({
    where: { id: profileId },
  });

  if (!existingProfile) {
    throw new Error("Profile not found");
  }

  const profileUpdateData = contactData
    ? {
        ...(contactData.name ? { name: contactData.name } : {}),
        ...(contactData.email ? { email: contactData.email } : {}),
        ...(contactData.phoneNumber
          ? { phoneNumber: contactData.phoneNumber }
          : {}),
        ...(contactData.company ? { company: contactData.company } : {}),
        ...(contactData.jobTitle ? { jobTitle: contactData.jobTitle } : {}),
      }
    : {};

  const profile =
    Object.keys(profileUpdateData).length > 0
      ? await prisma.profile.update({
          where: { id: profileId },
          data: profileUpdateData,
        })
      : existingProfile;

  const whiteLabel = await getWhiteLabelByEventId(eventId);
  const whiteLabelId = whiteLabel?.id;

  if (!whiteLabelId) {
    throw new Error("WhiteLabel not found for event");
  }

  let userId = profile.userId;

  if (!userId && profile.email) {
    await adoptUnscopedUser(profile.email, whiteLabelId);
    const user = await prisma.user.upsert({
      where: {
        email_accessibleWhiteLabelId: {
          email: profile.email.toLowerCase(),
          accessibleWhiteLabelId: whiteLabelId,
        },
      },
      update: {
        profiles: {
          connect: { id: profileId },
        },
      },
      create: {
        name: profile.name || "",
        email: profile.email.toLowerCase(),
        phoneNumber: profile.phoneNumber || undefined,
        accessibleWhiteLabelId: whiteLabelId,
        profiles: {
          connect: { id: profileId },
        },
      },
    });

    userId = user.id;

    await prisma.profile.update({
      where: { id: profileId },
      data: {
        userId: user.id,
      },
    });
  }

  const currentParticipantGroupIds = profile.participantGroupIds || [];
  const newParticipantGroupIds = [
    ...new Set([...currentParticipantGroupIds, ...participantGroupIds]),
  ];

  await prisma.profile.update({
    where: { id: profileId },
    data: {
      participantGroupIds: {
        set: newParticipantGroupIds,
      },
    },
  });

  invalidateCheckinRosterForProfile(profileId).catch(() => undefined);

  await Promise.all(
    participantGroupIds.map(async (groupId) => {
      const participantGroup = await prisma.participantGroup.findUnique({
        where: { id: groupId },
        select: { participantIds: true },
      });

      if (
        participantGroup &&
        !participantGroup.participantIds.includes(profileId)
      ) {
        await prisma.participantGroup.update({
          where: { id: groupId },
          data: {
            participantIds: {
              set: [...participantGroup.participantIds, profileId],
            },
          },
        });
      }
    })
  );

  if (!userId) {
    return {};
  }

  const updatedProfile = await prisma.profile.findUnique({
    where: { id: profileId },
  });

  if (!updatedProfile) {
    return {};
  }

  const result = await setupParticipantFeatures(
    updatedProfile,
    participantGroupIds,
    eventId,
    whiteLabelId,
    userId
  );

  return { token: result?.token };
};

/**
 * Confirms ticket-invitation attendees after checkout completes.
 * When the invitation has a form, the attendee stays pending until the form
 * is submitted; participant groups are only assigned once confirmed.
 */
export const syncInvitationAttendeesAfterOrderComplete = async (
  orderId: string
) => {
  const orderItems = await prisma.orderItem.findMany({
    where: {
      orderId,
      invitationAttendeeId: { not: null },
    },
    select: {
      invitationAttendeeId: true,
    },
  });

  const attendeeIds = [
    ...new Set(
      orderItems
        .map((item) => item.invitationAttendeeId)
        .filter((id): id is string => Boolean(id))
    ),
  ];

  if (attendeeIds.length === 0) {
    return;
  }

  await Promise.all(
    attendeeIds.map(async (attendeeId) => {
      const attendee = await prisma.invitationAttendee.findUnique({
        where: { id: attendeeId },
        include: {
          invitation: {
            include: {
              formEntity: {
                include: {
                  formTemplate: {
                    select: { id: true },
                  },
                },
              },
            },
          },
        },
      });

      if (!attendee || attendee.state === InvitationAttendeeState.CONFIRMED) {
        return;
      }

      const formTemplateId =
        attendee.invitation.formEntity?.formTemplate?.id ?? null;
      const hasInvitationForm = Boolean(formTemplateId);
      const formCompleted = hasInvitationForm
        ? await isInvitationFormCompleted(attendeeId, formTemplateId!)
        : true;
      const waitForForm = hasInvitationForm && !formCompleted;

      await responseToInvitation(
        attendeeId,
        InvitationAttendeeState.CONFIRMED,
        waitForForm
      );
    })
  );
};

/**
 * Atomically transitions an invitation attendee to the target state, returning
 * `true` only if this call actually changed the row. Concurrent callers (e.g.
 * email link scanners that prefetch RSVP links several times) will see
 * `count === 0` after the first writer wins and can short-circuit without
 * re-running side effects like trigger dispatches or order creation.
 *
 * Returns `false` when the parent invitation has expired, so the calling page
 * can render the "expired" UX instead of advancing the attendee's state.
 *
 * Expiry is checked in application code (not via a relation filter on
 * `updateMany`). Relation filters in MongoDB `updateMany` where-clauses are
 * unreliable here and previously caused claims to always miss — leaving
 * private no-form RSVPs stuck on SENT with no order created.
 */
export const claimInvitationResponseState = async (
  attendeeId: string,
  targetState: InvitationAttendeeState
): Promise<boolean> => {
  const attendee = await prisma.invitationAttendee.findUnique({
    where: { id: attendeeId },
    select: {
      state: true,
      invitation: { select: { expiryDate: true } },
    },
  });

  if (!attendee) return false;
  if (attendee.state === targetState) return false;
  if (isInvitationExpired(attendee.invitation?.expiryDate)) return false;

  const result = await prisma.invitationAttendee.updateMany({
    where: {
      id: attendeeId,
      state: { not: targetState },
    },
    data: { state: targetState },
  });
  return result.count > 0;
};

export class InvitationExpiredError extends Error {
  code = "INVITATION_EXPIRED" as const;
  expiryDate: Date | null;
  invitationId: string;

  constructor(invitationId: string, expiryDate: Date | null) {
    super("Invitation has expired");
    this.name = "InvitationExpiredError";
    this.invitationId = invitationId;
    this.expiryDate = expiryDate;
  }
}

export const responseToInvitation = async (
  id: string,
  response: InvitationAttendeeState,
  dontCompleteOrder?: boolean
) => {
  try {
    // Refuse to mutate attendee/order state once the invitation has expired.
    // Caller is responsible for surfacing the "expired" UX; we never want a
    // late RSVP click to silently flip an attendee back to CONFIRMED/MAYBE
    // after the admin's cutoff.
    const existingAttendee = await prisma.invitationAttendee.findUnique({
      where: { id },
      select: {
        invitationId: true,
        invitation: { select: { expiryDate: true } },
      },
    });

    if (!existingAttendee) {
      throw new Error("Invitation attendee not found");
    }

    if (isInvitationExpired(existingAttendee.invitation?.expiryDate)) {
      throw new InvitationExpiredError(
        existingAttendee.invitationId,
        existingAttendee.invitation?.expiryDate ?? null
      );
    }

    const attendeeState =
      dontCompleteOrder && response === InvitationAttendeeState.CONFIRMED
        ? InvitationAttendeeState.PENDING_INVITEE
        : response;

    const updatedInvitationAttendee = await prisma.invitationAttendee.update({
      where: {
        id,
      },
      data: {
        state: attendeeState,
      },
      include: {
        invitation: { include: { invitationTickets: true, rsvps: true } },
        contact: true,
      },
    });
    let newOrder;
    let fromState: TransitionState = TransitionState.CREATED;

    const hasInvitationTickets =
      (updatedInvitationAttendee?.invitation?.invitationTickets?.length ?? 0) >
      0;

    const orderState: OrderStatus =
      hasInvitationTickets ||
      (response === InvitationAttendeeState.CONFIRMED && !dontCompleteOrder)
        ? OrderStatus.COMPLETED
        : response === InvitationAttendeeState.DECLINED
        ? OrderStatus.CANCELED
        : OrderStatus.ON_GOING;

    const orderItemState: OrderItemStatus = (() => {
      switch (attendeeState) {
        case InvitationAttendeeState.DECLINED:
          return OrderItemStatus.CANCELED;
        case InvitationAttendeeState.MAYBE:
          return OrderItemStatus.NEW;
        case InvitationAttendeeState.PENDING_INVITEE:
          return OrderItemStatus.RESERVED;
        case InvitationAttendeeState.CONFIRMED:
          return OrderItemStatus.CONFIRMED;
        default:
          return OrderItemStatus.CANCELED;
      }
    })();

    const order = await prisma.order.findFirst({
      where: {
        orderItems: {
          some: {
            invitationAttendeeId: updatedInvitationAttendee.id,
          },
        },
      },
      include: {
        orderItems: true,
      },
    });

    if (order) {
      newOrder = await prisma.order.update({
        where: {
          id: order?.id,
        },
        data: {
          status: orderState,
          ...(!hasInvitationTickets
            ? {
                orderItems: {
                  upsert: {
                    where: {
                      id: order.orderItems?.[0].id,
                    },
                    create: {
                      invitationAttendeeId: updatedInvitationAttendee.id,
                      status: orderItemState,
                    },
                    update: {
                      status: orderItemState,
                    },
                  },
                },
              }
            : {}),
        },
        include: {
          orderItems: true,
          formResults: true,
        },
      });

      if (hasInvitationTickets) {
        await prisma.orderItem.updateMany({
          where: { invitationAttendeeId: updatedInvitationAttendee.id },
          data: { status: orderItemState },
        });
      }
    } else {
      const newNumber = await generateOrderNumber(
        updatedInvitationAttendee.invitation.eventId
      );
      newOrder = await prisma.order.create({
        data: {
          status: orderState,
          number: newNumber,
          type: OrderType.INVITATION,
          eventId: updatedInvitationAttendee.invitation.eventId,
          // Inherit the invitation's fixed cutoff so the expire-order cron can
          // pick this order up once the deadline passes. Orders in
          // COMPLETED/CANCELED won't be touched by the cron, which already
          // filters by status.
          ...(updatedInvitationAttendee.invitation.expiryDate
            ? { expiryDate: updatedInvitationAttendee.invitation.expiryDate }
            : {}),
          ...(!hasInvitationTickets
            ? {
                orderItems: {
                  create: {
                    invitationAttendeeId: updatedInvitationAttendee.id,
                    status: orderItemState,
                  },
                },
              }
            : {}),
        },
        include: {
          orderItems: true,
          formResults: true,
        },
      });
    }
    const latestTransition = await getLatestTransition(
      updatedInvitationAttendee.id
    );
    if (latestTransition) {
      fromState = latestTransition?.toState;

      await prisma.transition.update({
        where: {
          id: latestTransition.id,
        },
        data: {
          mostRecent: false,
        },
      });
    }

    const transition = await prisma.transition.create({
      data: {
        fromState: fromState,
        toState: attendeeState as keyof typeof TransitionState,
        mostRecent: true,
        transitionalId: updatedInvitationAttendee.id,
        transitionalType: TransitionalModel.INVITATION_ATTENDEE,
        createdBy: id,
        metaData: {
          details: `State changed to ${attendeeState}`,
          relatedOrderId: newOrder?.id,
          relatedInvitationId: updatedInvitationAttendee.invitationId,
        },
      },
    });
    await createOrderTransition({
      status: newOrder.status,
      orderId: newOrder?.id,
      createdBy: id,
      metaData: {
        details: `State changed to ${newOrder.status}`,
        relatedInvitationId: updatedInvitationAttendee.invitationId,
        relatedAttendeeId: updatedInvitationAttendee.id,
      },
    });
    newOrder?.orderItems?.map(async (orderItem: OrderItem) => {
      await createOrderItemTransition({
        status: orderItemState,
        orderItemId: orderItem?.id,
        createdBy: id,
        metaData: {
          details: `State changed to ${orderItemState}`,
          relatedInvitationId: updatedInvitationAttendee.invitationId,
          relatedAttendeeId: updatedInvitationAttendee.id,
        },
      });
    });
    let token: string | undefined;
    // Add profile to participant groups only when attendee is fully confirmed
    if (
      attendeeState === InvitationAttendeeState.CONFIRMED &&
      updatedInvitationAttendee.invitation.participantGroupIds &&
      updatedInvitationAttendee.invitation.participantGroupIds.length > 0
    ) {
      const participantProfile = await resolveInvitationAttendeeParticipantProfile(
        updatedInvitationAttendee,
        updatedInvitationAttendee.invitation.eventId
      );

      if (participantProfile) {
        const result = await addInvitationAttendeeToParticipantGroups(
          participantProfile.profileId,
          updatedInvitationAttendee.invitation.participantGroupIds,
          updatedInvitationAttendee.invitation.eventId,
          participantProfile.contact
        );
        token = result.token;
      }
    }

    return {
      invitationAttendee: updatedInvitationAttendee,
      order: newOrder,
      transition,
      ...(token && { token }),
    };
  } catch (e: any) {
    // Preserve the typed expiry error so callers can render the dedicated
    // "expired" UX instead of a generic 500.
    if (e instanceof InvitationExpiredError) throw e;
    throw new Error(e);
  }
};

export const getInvitationUsedQuantity = async (id: string) => {
  try {
    const quantities = await prisma.order.count({
      where: {
        status: OrderStatus.COMPLETED,
        orderItems: {
          some: {
            invitationAttendeeId: id,
          },
        },
      },
    });
    return quantities;
  } catch (e: any) {
    throw new Error(e);
  }
};

export const saveInvitationRSVPLink = async (
  invitationId: string,
  rsvpValue: RSVPvalues,
  link: string
) => {
  try {
    const invitation = await prisma.invitation.findUnique({
      where: { id: invitationId },
      select: { eventId: true },
    });

    if (!invitation) {
      throw new Error("Invitation not found");
    }

    await unlinkRSVPTemplatesV2ForInvitation(
      invitation.eventId,
      invitationId,
      rsvpValue
    );

    const template = await prisma.rSVP.update({
      where: {
        invitationId_value: {
          invitationId: invitationId,
          value: rsvpValue,
        },
      },
      data: {
        link: link,
        thankYouPageTemplateId: null,
      },
    });
    return template;
  } catch (error: any) {
    console.error("Error saving template:", error);
    throw new Error(error);
  }
};

export const getInvitationsRSVPTemplatesByRSVPvalue = async (
  invitationId: string,
  value: RSVPvalues
) => {
  try {
    const rsvp = await prisma.rSVP.findUnique({
      where: {
        invitationId_value: {
          invitationId: invitationId,
          value: value,
        },
      },
      include: {
        thankYouPageTemplate: true,
      },
    });

    if (!rsvp) {
      return null;
    }

    if (rsvp.link) {
      return {
        ...rsvp,
        templateV2: null,
      };
    }

    if (rsvp.thankYouPageTemplate) {
      return {
        ...rsvp,
        templateV2: rsvp.thankYouPageTemplate,
      };
    }

    const invitation = await prisma.invitation.findUnique({
      where: { id: invitationId },
      select: { eventId: true },
    });

    if (!invitation) {
      throw new Error("Invitation not found");
    }

    const templateId = await getRSVPTemplateV2IdForInvitation(
      invitation.eventId,
      invitationId,
      value
    );

    if (!templateId) {
      return {
        ...rsvp,
        templateV2: null,
      };
    }

    const template = await prisma.templateV2.findUnique({
      where: { id: templateId },
    });

    return {
      ...rsvp,
      templateV2: template,
    };
  } catch (error: any) {
    console.error("Error getting template:", error);
    throw new Error(error);
  }
};
export const getInvitationResponseHTMLPage = async (invitationId: string) => {
  try {
    const invitation = await prisma.invitation.findUnique({
      where: { id: invitationId },
      include: {
        translations: true,
        rsvps: true,
      },
    });

    if (!invitation) {
      return null;
    }

    const linkedTemplates = await getTemplateV2LinkedToInvitation(
      invitation.eventId,
      invitationId
    );

    const responsePageTemplate =
      linkedTemplates.find(
        (template) =>
          template.type === TemplateV2Type.HTML &&
          (template.templateFor ===
            TemplateV2For.INVITATION_RESPONSE_PAGE_HTML ||
            template.templateFor === TemplateV2For.INVITATION_LANDING_PAGE_HTML)
      ) ??
      (await resolveTemplateV2({
        type: TemplateV2Type.HTML,
        templateFor: TemplateV2For.INVITATION_RESPONSE_PAGE_HTML,
        scopes: { eventId: invitation.eventId },
        targetContexts: [
          {
            entityType: TemplateTargetEntityType.INVITATION,
            entityId: invitationId,
          },
        ],
      }));

    return {
      invitationId,
      invitation,
      templateV2: responsePageTemplate,
    };
  } catch (error: any) {
    console.error("Error getting invitation response page:", error);
    throw new Error(error);
  }
};
export const getInvitationResponseCount = async (
  invitationId: string,
  attendeesCategoryId?: string
) => {
  try {
    const response = await prisma.invitationAttendee.groupBy({
      by: ["state"],
      where: {
        invitationId: invitationId,
        ...(attendeesCategoryId && {
          attendeesCategoryId: attendeesCategoryId,
        }),
      },
      _count: true,
    });
    return response;
  } catch (error: any) {
    console.error("Error getting response count:", error);
    throw new Error(error);
  }
};

export const getInvitationsFromEvents = async (eventIds: string[]) => {
  try {
    const invitations = await prisma.invitation.findMany({
      where: {
        eventId: {
          in: eventIds,
        },
      },
    });
    return invitations;
  } catch (error: any) {
    console.error("Error getting invitations:", error);
    throw new Error(error);
  }
};

interface PaginationOptions {
  page?: number;
  limit?: number;
  search?: string;
  filters?: ColumnFilter[];
}

interface ColumnFilter {
  column: string;
  operator:
    | "equals"
    | "contains"
    | "startsWith"
    | "endsWith"
    | "in"
    | "between"
    | "greaterThan"
    | "lessThan";
  value: any;
  label?: string;
}

type StatusCounts = InvitationAttendeeStatusCounts;

interface PaginatedResult<T> {
  data: T[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
    hasNext: boolean;
    hasPrev: boolean;
  };
  statusCounts: StatusCounts;
}

export const getInvitationAttendeeReport = async (
  eventId: string,
  options: PaginationOptions = {},
  lang = "en"
): Promise<PaginatedResult<any>> => {
  try {
    const prisma = reportingPrisma;
    const { page = 1, limit = 10, search, filters = [] } = options;
    const skip = (page - 1) * limit;

    // Build base where clause
    const whereClause: any = {
      invitation: {
        eventId: eventId,
      },
    };

    // Add search conditions
    if (search) {
      whereClause.OR = [
        {
          contact: {
            name: {
              contains: search,
              mode: "insensitive" as const,
            },
          },
        },
        {
          contact: {
            email: {
              contains: search,
              mode: "insensitive" as const,
            },
          },
        },
        {
          contact: {
            phoneNumber: {
              contains: search,
              mode: "insensitive" as const,
            },
          },
        },
        {
          invitation: {
            translations: {
              some: {
                language: {
                  code: lang,
                },
                name: {
                  contains: search,
                  mode: "insensitive" as const,
                },
              },
            },
          },
        },
        {
          attendeeCategory: {
            contactBookGroup: {
              name: {
                contains: search,
                mode: "insensitive" as const,
              },
            },
          },
        },
      ];
    }

    // Add advanced filter conditions
    if (filters.length > 0) {
      const filterConditions: any[] = [];

      filters.forEach((filter) => {
        const condition = buildFilterCondition(filter, lang);
        if (condition) {
          filterConditions.push(condition);
        }
      });

      if (filterConditions.length > 0) {
        whereClause.AND = filterConditions;
      }
    }

    // Get status counts with the same filters
    const statusCounts = await getInvitationAttendeeStatusCounts(whereClause);

    const total = statusCounts.total;
    const invitations = await prisma.invitationAttendee.findMany({
      where: whereClause,
      include: {
        contact: true,
        attendeeCategory: {
          include: {
            contactBookGroup: {
              include: {
                fields: {
                  include: {
                    formTemplate: true,
                  },
                },
              },
            },
          },
        },
        invitation: {
          include: {
            translations: {
              where: {
                language: { code: lang },
              },
            },
          },
        },
      },
      skip: skip,
      take: limit,
      orderBy: {
        createdAt: "desc",
      },
    });

    const totalPages = Math.ceil(total / limit);

    return {
      data: invitations,
      pagination: {
        page,
        limit,
        total,
        totalPages,
        hasNext: page < totalPages,
        hasPrev: page > 1,
      },
      statusCounts,
    };
  } catch (error: any) {
    console.error("Error getting invitations:", error);
    throw new Error(error);
  }
};

export {
  getInvitationAttendeeStatusCounts,
  getInvitationAttendeeStatusCountsByEventId,
  type InvitationAttendeeStatusCounts,
};

// Helper function to build filter conditions
function buildFilterCondition(filter: ColumnFilter, lang: string): any {
  const { column, operator, value } = filter;

  if (!value || (Array.isArray(value) && value.length === 0)) {
    return null;
  }

  switch (column) {
    case "name":
      return {
        contact: {
          is: buildTextFilter("name", operator, value),
        },
      };

    case "email":
      return {
        contact: {
          is: buildTextFilter("email", operator, value),
        },
      };

    case "phoneNumber":
      return {
        contact: {
          is: buildTextFilter("phoneNumber", operator, value),
        },
      };

    case "invitationName":
      return {
        invitation: {
          translations: {
            some: {
              language: { code: lang },
              ...buildTextFilter("name", operator, value),
            },
          },
        },
      };

    case "categoryName":
      return {
        attendeeCategory: {
          contactBookGroup: buildTextFilter("name", operator, value),
        },
      };

    case "state":
      return buildSelectFilter("state", operator, value);

    case "createdAt":
    case "updatedAt":
      return buildDateFilter(column, operator, value);

    default:
      return null;
  }
}

function buildTextFilter(field: string, operator: string, value: any): any {
  const fieldPath = field.split(".");
  const fieldName = fieldPath[fieldPath.length - 1];

  switch (operator) {
    case "equals":
      return { [fieldName]: { equals: value, mode: "insensitive" } };
    case "contains":
      return { [fieldName]: { contains: value, mode: "insensitive" } };
    case "startsWith":
      return { [fieldName]: { startsWith: value, mode: "insensitive" } };
    case "endsWith":
      return { [fieldName]: { endsWith: value, mode: "insensitive" } };
    default:
      return { [fieldName]: { contains: value, mode: "insensitive" } };
  }
}

function buildSelectFilter(field: string, operator: string, value: any): any {
  switch (operator) {
    case "equals":
      return { [field]: value };
    case "in":
      return { [field]: { in: Array.isArray(value) ? value : [value] } };
    default:
      return { [field]: value };
  }
}

function buildDateFilter(field: string, operator: string, value: any): any {
  switch (operator) {
    case "equals":
      const date = new Date(value);
      const nextDay = new Date(date);
      nextDay.setDate(date.getDate() + 1);
      return {
        [field]: {
          gte: date,
          lt: nextDay,
        },
      };
    case "between":
      if (Array.isArray(value) && value.length === 2) {
        return {
          [field]: {
            gte: new Date(value[0]),
            lte: new Date(value[1]),
          },
        };
      }
      return null;
    case "greaterThan":
      return { [field]: { gt: new Date(value) } };
    case "lessThan":
      return { [field]: { lt: new Date(value) } };
    default:
      return null;
  }
}
